import {
  defaultLocale,
  getLocalizedPublicPath,
  isLocalizablePublicPath,
  normalizeSupportedLocale,
} from '../locales';
import { formatInrNumber, GST_RATE_PERCENT, SUBSCRIPTION_PRICING } from '../pricing';

import type { FaqItem, SeoRouteDefinition } from './types';

export const DEFAULT_SITE_URL = 'https://leaderdaddy.com';
export const SITE_NAME = 'LeaderDaddy';
export const PRODUCT_NAME = 'LeaderDaddy';
export const PRODUCT_SHORT_NAME = 'LeaderDaddy';
export const SITE_DESCRIPTION =
  'LeaderDaddy is a lead management system and sales CRM for teams that need lead tracking, follow-up automation, WhatsApp-ready workflows, reporting, and structured team execution.';
export const DEFAULT_OG_IMAGE_PATH = '/og-image.png';
export const SUPPORT_EMAIL = 'contact@toolcross.com';
export const SUPPORT_PHONE = '+918796085665';
export const SUPPORT_PHONE_DISPLAY = '+91 8796085665';
export const SUPPORT_WHATSAPP_URL = `https://wa.me/${SUPPORT_PHONE.replace(/\D/g, '')}`;
export const SUPPORT_REGION = 'India';
export const COMPANY_NAME = 'Toolcross LLP';
export const DEFAULT_YEARLY_PRICE_INR = String(SUBSCRIPTION_PRICING.finalPriceInr);
export const DEFAULT_YEARLY_PRICE_DISPLAY_INR = formatInrNumber(SUBSCRIPTION_PRICING.finalPriceInr);

export const TARGET_KEYWORDS = [
  'lead management system',
  'lead crm',
  'crm for sales team',
  'sales lead management software',
  'lead tracking software',
  'lead management app',
  'best crm for small business',
  'lead follow up software',
  'lead management for sales team',
  'online lead management software',
  'crm with whatsapp integration',
  'sales automation software',
  'zoho crm alternative',
  'hubspot alternative for sales',
  'leadsquared alternative',
  'spreadsheet to crm migration',
];

export const commonFeatureList = [
  'Lead capture and lead tracking',
  'List view and kanban view pipeline management',
  'Follow-up reminders and overdue lead visibility',
  'Team management for admins, managers, and salesmen',
  'CRM reporting and sales dashboard insights',
  'WhatsApp-ready notification workflows',
  'Role-based access control and company settings',
  'AI-assisted sales guidance and lead analysis workflows',
];

export const commonBestFor = [
  'Sales teams',
  'Small businesses',
  'Agencies',
  'Consultants',
  'Education admissions teams',
  'Real estate lead follow-up teams',
];

export const homepageFaqs: FaqItem[] = [
  {
    question: 'What is LeaderDaddy?',
    answer:
      'LeaderDaddy is a lead management system and lead CRM that helps sales teams capture leads, track follow-ups, manage assignments, review reports, and coordinate work from one workspace.',
  },
  {
    question: 'Who should use this lead management software?',
    answer:
      'It is best for small businesses, agencies, consultants, field sales teams, education admissions teams, and growing companies that need a cleaner lead tracking process than spreadsheets.',
  },
  {
    question: 'Does the CRM support WhatsApp workflows?',
    answer:
      'Yes. Connect an eligible WhatsApp Business account to manage conversations, sync templates, and send targeted or scheduled broadcasts. Meta messaging charges and account limits apply separately from the LeaderDaddy subscription.',
  },
  {
    question: 'Why is this better than spreadsheets for lead tracking?',
    answer:
      'Spreadsheets hide ownership, make follow-ups easy to miss, and do not give managers real-time visibility. LeaderDaddy keeps every lead, activity, status, reminder, and assignee in one structured system.',
  },
  {
    question: 'Is there a free trial?',
    answer:
      'Yes. The product includes a 7-day free trial so teams can evaluate the CRM, team workflows, and reporting experience before upgrading.',
  },
  {
    question: 'Can managers and salesmen use the same CRM?',
    answer:
      'Yes. The product is designed for role-based collaboration across admins, managers, and salesmen so teams can share one lead workflow without losing control.',
  },
];

export const pricingFaqs: FaqItem[] = [
  {
    question: 'How is LeaderDaddy priced?',
    answer: `LeaderDaddy uses a simple annual subscription with a 7-day free trial. The current list price is INR ${DEFAULT_YEARLY_PRICE_DISPLAY_INR} per year, inclusive of ${GST_RATE_PERCENT}% GST.`,
  },
  {
    question: 'What features are included in the annual plan?',
    answer:
      'The annual plan covers lead tracking, dashboards, reports, team workflows, follow-up management, products, settings, and the WhatsApp-ready notification stack that is enabled for your workspace.',
  },
  {
    question: 'Can I start without a credit card?',
    answer:
      'The signup flow in this application activates a 7-day trial first, so teams can explore the CRM before completing a paid subscription.',
  },
];

function absoluteUrl(siteUrl: string, pathOrUrl: string) {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  const normalizedSiteUrl = siteUrl.replace(/\/$/, '');
  const normalizedPath = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${normalizedSiteUrl}${normalizedPath}`;
}

function resolveLanguageName(locale: string) {
  return locale.startsWith('hi') ? 'Hindi' : 'English';
}

function resolveLocalizedPublicUrlPath(pathname: string, locale: string) {
  return isLocalizablePublicPath(pathname)
    ? getLocalizedPublicPath(pathname, normalizeSupportedLocale(locale) ?? defaultLocale)
    : pathname;
}

function toGraphList(items?: string[]) {
  if (!items?.length) {
    return undefined;
  }

  return items.map(item => item.trim()).filter(Boolean);
}

export function getDefaultSiteUrl() {
  return DEFAULT_SITE_URL.replace(/\/$/, '');
}

export function buildSeoGraph(
  route: SeoRouteDefinition,
  siteUrl: string,
  options?: {
    locale?: string | null;
  }
) {
  const normalizedSiteUrl = siteUrl.replace(/\/$/, '');
  const resolvedLocale = normalizeSupportedLocale(options?.locale) ?? defaultLocale;
  const localeLanguage = resolveLanguageName(resolvedLocale);
  const canonicalPath = route.canonicalPath || route.path;
  const canonicalUrl = absoluteUrl(normalizedSiteUrl, canonicalPath);
  const imageUrl = absoluteUrl(normalizedSiteUrl, route.imagePath || DEFAULT_OG_IMAGE_PATH);

  const organizationSchema = {
    '@type': 'Organization',
    '@id': `${normalizedSiteUrl}/#publisher`,
    name: COMPANY_NAME,
    legalName: COMPANY_NAME,
    url: `${normalizedSiteUrl}${getLocalizedPublicPath('/', defaultLocale)}`,
    logo: absoluteUrl(normalizedSiteUrl, '/pwa/icon-512x512.png'),
    contactPoint: [
      {
        '@type': 'ContactPoint',
        email: SUPPORT_EMAIL,
        url: SUPPORT_WHATSAPP_URL,
        contactType: 'WhatsApp customer support',
        areaServed: SUPPORT_REGION,
        availableLanguage: ['English', 'Hindi'],
      },
    ],
  };

  const websiteSchema = {
    '@type': 'WebSite',
    '@id': `${normalizedSiteUrl}/#website`,
    url: `${normalizedSiteUrl}${getLocalizedPublicPath('/', defaultLocale)}`,
    name: SITE_NAME,
    description: SITE_DESCRIPTION,
    inLanguage: resolvedLocale,
    publisher: { '@id': `${normalizedSiteUrl}/#publisher` },
  };

  const webPageSchema: Record<string, unknown> = {
    '@type': 'WebPage',
    '@id': `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: route.title,
    description: route.description,
    isPartOf: { '@id': `${normalizedSiteUrl}/#website` },
    publisher: { '@id': `${normalizedSiteUrl}/#publisher` },
    inLanguage: resolvedLocale,
    primaryImageOfPage: { '@id': `${canonicalUrl}#primaryimage` },
    about: { '@id': `${normalizedSiteUrl}/#brand` },
    keywords: route.keywords.join(', '),
    ...(route.lastModified ? { dateModified: route.lastModified } : {}),
  };

  const imageObjectSchema = {
    '@type': 'ImageObject',
    '@id': `${canonicalUrl}#primaryimage`,
    url: imageUrl,
    caption: route.title,
  };

  const graph: Record<string, unknown>[] = [
    organizationSchema,
    {
      '@type': 'Brand',
      '@id': `${normalizedSiteUrl}/#brand`,
      name: SITE_NAME,
      url: `${normalizedSiteUrl}${getLocalizedPublicPath('/', defaultLocale)}`,
    },
    websiteSchema,
    imageObjectSchema,
    webPageSchema,
  ];

  if (route.breadcrumbs?.length) {
    graph.push({
      '@type': 'BreadcrumbList',
      '@id': `${canonicalUrl}#breadcrumbs`,
      itemListElement: route.breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: absoluteUrl(
          normalizedSiteUrl,
          resolveLocalizedPublicUrlPath(crumb.path, resolvedLocale)
        ),
      })),
    });
  }

  const featureList = toGraphList(route.featureList);
  const bestFor = toGraphList(route.bestFor);
  if (route.softwareApplication) {
    const softwareApplication: Record<string, unknown> = {
      '@type': 'SoftwareApplication',
      '@id': `${normalizedSiteUrl}/#software`,
      name: PRODUCT_NAME,
      applicationCategory: route.applicationCategory || 'BusinessApplication',
      applicationSubCategory: route.softwareSubCategory || 'Lead Management CRM',
      operatingSystem: 'Web',
      url: `${normalizedSiteUrl}${getLocalizedPublicPath('/', defaultLocale)}`,
      description: route.summary || route.description,
      inLanguage: resolvedLocale,
      availableLanguage: [localeLanguage],
      featureList,
      audience: bestFor?.map(audienceType => ({
        '@type': 'Audience',
        audienceType,
      })),
      brand: {
        '@id': `${normalizedSiteUrl}/#brand`,
      },
      publisher: {
        '@id': `${normalizedSiteUrl}/#publisher`,
      },
    };

    if (route.offers?.length) {
      softwareApplication.offers = route.offers.map(offer => ({
        '@type': 'Offer',
        name: offer.name,
        description: offer.description,
        price: offer.price,
        priceCurrency: offer.priceCurrency,
        availability: offer.availability || 'https://schema.org/InStock',
        category: offer.category || 'Lead Management Software',
        url: absoluteUrl(
          normalizedSiteUrl,
          resolveLocalizedPublicUrlPath(offer.urlPath, resolvedLocale)
        ),
      }));
    }

    if (route.aggregateRating) {
      softwareApplication.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: route.aggregateRating.ratingValue,
        reviewCount: route.aggregateRating.reviewCount,
        bestRating: route.aggregateRating.bestRating || 5,
        worstRating: route.aggregateRating.worstRating || 1,
      };
    }

    graph.push(softwareApplication);
  }

  if (route.faqs?.length) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${canonicalUrl}#faq`,
      isPartOf: { '@id': `${canonicalUrl}#webpage` },
      inLanguage: resolvedLocale,
      mainEntity: route.faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });
  }

  return {
    canonicalUrl,
    imageUrl,
    graph: {
      '@context': 'https://schema.org',
      '@graph': graph,
    },
  };
}
